import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Button, Form, Input } from 'components/HookForm';
import Message from 'components/Message';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { isVisibleSkuEntity } from '../SkuMenuData.normalizers';

import { createSkuSubgroup } from '../SkuItems.helpers';
import { getErrorSnackbarDescriptionWithContactLink } from '../SkuErrorSnackbar.helpers';
import messages from './AddCategoryModal.messages';
import './AddCategoryModal.scss';

const { block, element } = bem('add-category-modal');

export const ADD_CATEGORY_MODAL_NAME = 'AddCategoryModal';

const submitCategory = async ({ watch, venueId, selectedMenu, dispatch, onSuccess, handleClose }) => {
  const categoryName = watch('categoryName');
  try {
    await createSkuSubgroup(venueId, {
      groupId: selectedMenu.id,
      name: categoryName.trim(),
      enabled: true,
    });
    dispatch(
      createSuccessSnackbar(messages.categoryCreated, {
        categoryName,
        strong: chunks => <strong>{chunks}</strong>,
      })
    );
    onSuccess?.();
    handleClose();
  } catch (error) {
    reportError('Error creating SKU category', error);
    dispatch(
      createErrorSnackbar(
        messages.categoryCreateError,
        { strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true },
        getErrorSnackbarDescriptionWithContactLink(messages.categoryCreateErrorDescription)
      )
    );
  }
};

const AddCategoryModal = ({ menus, venueId, defaultMenuId, onSuccess, closeModal }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const enabledMenus = (menus || []).filter(isVisibleSkuEntity);
  const defaultMenu = defaultMenuId ? enabledMenus.find(m => String(m.id) === String(defaultMenuId)) : null;
  const [selectedMenu, setSelectedMenu] = useState(defaultMenu || null);

  const handleClose = () => {
    closeModal();
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={messages.title} />
        </h3>
        <button
          type="button"
          aria-label={intl.formatMessage(messages.closeAriaLabel)}
          className={element('close-icon')}
          onClick={handleClose}>
          <Isvg src={ICONS.cross} />
        </button>
      </header>
      <Form id="add-category-form" defaultValues={{ categoryName: '' }}>
        {({ formState, handleSubmit, watch }) => {
          const { isDirty, isSubmitting } = formState;
          const canSubmit = isDirty && selectedMenu;

          const handleFormSubmit = () =>
            submitCategory({ watch, venueId, selectedMenu, dispatch, onSuccess, handleClose });

          return (
            <>
              <div className={element('form-fields')}>
                <Input
                  name="categoryName"
                  disabled={isSubmitting}
                  label={messages.categoryNameLabel}
                  placeholder={messages.categoryNamePlaceholder}
                  type="text"
                  required
                  showRequiredLabel
                  validate={{
                    message: messages.categoryNameRequired,
                    value: value => Boolean(value?.trim()),
                  }}
                />
                <div className={element('menu-section')} data-testid="menu-section">
                  <div className={element('menu-label')}>
                    <Message message={messages.selectMenu} />
                    <Message as="span" className={element('required-label')} message={messages.required} />
                  </div>
                  <Dropdown>
                    {() => (
                      <>
                        <DropdownToggle>
                          <span className={element(selectedMenu ? 'dropdown-value' : 'dropdown-placeholder')}>
                            {selectedMenu ? selectedMenu.name : intl.formatMessage(messages.selectMenu)}
                          </span>
                        </DropdownToggle>
                        <DropdownMenu>
                          {enabledMenus.map(menu => (
                            <DropdownItem
                              key={menu.id}
                              isSelected={selectedMenu?.id === menu.id}
                              onClick={() => setSelectedMenu(menu)}>
                              {menu.name}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </>
                    )}
                  </Dropdown>
                </div>
              </div>
              <footer className={element('footer')}>
                <Message
                  as={Button}
                  className={element('save-btn')}
                  disabled={!canSubmit || isSubmitting}
                  onClick={handleSubmit(handleFormSubmit)}
                  message={messages.save}
                  styled="primary"
                  type={Button.TYPES.SUBMIT}
                />
              </footer>
            </>
          );
        }}
      </Form>
    </div>
  );
};

AddCategoryModal.propTypes = {
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      enabled: PropTypes.bool,
    })
  ),
  venueId: PropTypes.number.isRequired,
  defaultMenuId: PropTypes.string,
  onSuccess: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
};

export default AddCategoryModal;
